import { useState, useEffect } from "react";
import { BsSearch } from "react-icons/bs";
import { MdKeyboardArrowRight } from "react-icons/md";
import DateRangeSelector from "./DateRangeSelector";
import MultiFilter from "./MultiFilter";
import { OptionType } from "./MultiFilter";
import LineChart from "./LineChart";
import CircularChart from "./CircularChart";
import PieCharts from "./PieChart";
import {
  useFetchDashboardFilterQuery,
  useFetchDashboardStatsMutation,
  useFetchRegionFilterQuery,
  useFetchFilterBasedOnBusinessUnitQuery,
} from "@services";
import { useTypedSelector } from "@hooks";
import ConversionCharts from "./ConversionCharts";
import { sortedByName } from "@/utils";

import JobOfferArt from "@assets/images/dashboard/job-offer.png";
import PackedBubbleChart from "@/components/PackedBubbleChart";


const Dashboard = () => {
  const [businessUnit, setBusinessUnit] = useState<OptionType[]>([]);
  const [region, setRegion] = useState<OptionType[]>([]);
  const [country, setCountry] = useState<OptionType[]>([]);
  const [gender, setGender] = useState<OptionType[]>([]);
  const [companyJobFunctions, setCompanyJobFunctions] = useState<OptionType[]>([]);
  const [grade, setGrade] = useState<OptionType[]>([]);
  const [jobOfferStatus, setJobOfferStatus] = useState<OptionType[]>([]);
  const [admin, setAdmin] = useState<OptionType[]>([]);
  const { user, permissions } = useTypedSelector(state => state.auth);
  const { id: company_id } = user?.company || {};
  const [showPicker, setShowPicker] = useState(false);
  const [range, setRange] = useState<{ startDate: string; endDate: string }>({
    startDate: "",
    endDate: "",
  });

  const {
    data: filterData,
    error: filterError,
    isLoading: filterLoading,
    isSuccess: filterSuccess,
  } = useFetchDashboardFilterQuery({ company_id });

  const {
    data: filterRegion,
    error: filterRegionError,
    isLoading: filterRegionLoading,
    isSuccess: filterRegionSuccess,
  } = useFetchRegionFilterQuery({ company_id });

  const {
    data: filterBasedOnBusinessUnit,
    error: filterErrorBasedOnBusinessUnit,
    isLoading: filterLoadingBasedOnBusinessUnit,
    isSuccess: filterSuccessBasedOnBusinessUnit,
  } = useFetchFilterBasedOnBusinessUnitQuery({ company_id });

  const filteredData = filterBasedOnBusinessUnit?.data?.filter((item: any) =>
    businessUnit.some(bu => bu.label === item.name)
  );
  const myregion = filteredData?.flatMap((d: any) => d.regions);

  // Filter regions based on selected region labels
  const myCountry = myregion?.filter((item: any) =>
    region.some(r => r.label === item.name)
  );
  const allCountries =
    myCountry?.flatMap((region: any) => region.countries) || [];

  const [fetchDashboardStats, { data: statsData }] =
    useFetchDashboardStatsMutation();

  useEffect(() => {
    if (company_id) {
      const hasFilters =
        jobOfferStatus.length ||
        companyJobFunctions.length ||
        country.length ||
        grade.length ||
        businessUnit.length ||
        region.length ||
        gender.length ||
        (range.startDate && range.endDate);

      const body = hasFilters
        ? {
          statuses: jobOfferStatus.map(item => item.value),
          company_job_functions: companyJobFunctions.map(item => item.label),
          countries: country.map(item => item.label),
          grades: grade.map(item => item.label),
          business_units: businessUnit.map(item => item.label),
          region: region.map(item => item.label),
          gender: gender.map(item => item.label.toUpperCase()),
          startDate: range?.startDate,
          endDate: range?.endDate,
        }
        : {}; // No filters = full data

      fetchDashboardStats({ company_id, body });
    }
  }, [
    company_id,
    jobOfferStatus,
    companyJobFunctions,
    country,
    grade,
    businessUnit,
    region,
    gender,
    range,
  ]);

  // const formatFullDate = (dateStr: string) => {
  //   const date = new Date(dateStr);
  //   return format(date, "EEEE, do MMMM");
  // };

  const formatFullDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric", // Add this for clarity
    });
  };

  const businessUnitData = Object.entries(
    statsData?.data?.by_business_unit || {}
  ).map(([name, value]) => ({ name, value: Number(value) }));

  const regionData = Object.entries(statsData?.data?.by_region || {}).map(
    ([name, value]) => ({ name, value: Number(value) })
  );

  const countryData = Object.entries(statsData?.data?.by_country || {}).map(
    ([name, value]) => ({ name, value: Number(value) })
  );

  const gradeData = Object.entries(statsData?.data?.by_grade || {}).map(
    ([name, value]) => ({ name, value: Number(value) })
  );

  const functionData = Object.entries(statsData?.data?.by_company_function || {}).map(
    ([name, value]) => ({ name, value: Number(value) })
  );

  const genderData = Object.entries(statsData?.data?.by_gender || {}).map(
    ([name, value]) => ({ name, value: Number(value) })
  );

  const adminData = Object.entries(statsData?.data?.by_creator || {}).map(
    ([name, value]) => ({ name, value: Number(value) })
  );

  const offerData = Object.entries(statsData?.data?.by_status || {}).map(
    ([name, value]) => ({ name, value: Number(value) })
  );

  // Clear region and country when business unit is empty
  useEffect(() => {
    if (businessUnit.length === 0) {
      setRegion([]);
      setCountry([]);
    }
  }, [businessUnit]);

  // Clear country when region is empty
  useEffect(() => {
    if (region.length === 0) {
      setCountry([]);
    }
  }, [region]);

  // ✅ Jab company_id change ho, filters reset + fetch fresh data
  useEffect(() => {
    setBusinessUnit([]);
    setRegion([]);
    setCountry([]);
    setGender([]);
    setCompanyJobFunctions([]);
    setGrade([]);
    setJobOfferStatus([]);
    setAdmin([]);
    setRange({ startDate: "", endDate: "" });
  }, [company_id]);

  interface ChartData {
    name: string;
    accepted: number;
    rejected: number;
  }

  const transformData = (
    apiData: Record<
      string,
      {
        accepted: number;
        rejected: number;
        acceptance_rate: number;
        rejection_rate: number;
      }
    >
  ): ChartData[] => {
    if (!apiData) return [];
    return Object.entries(apiData).map(([key, value]) => ({
      name: key,
      accepted: (value.acceptance_rate || 0) * 100, // 👈 match chart dataKey
      rejected: (value.rejection_rate || 0) * 100, // 👈 match chart dataKey
    }));
  };

  // inside Dashboard component
  const conversionByGrade = statsData?.data?.conversion_by_grade;
  const conversionByFunction = statsData?.data?.conversion_by_company_function;

  const newConversionByGrade: ChartData[] = transformData(conversionByGrade);
  const newConversionByFunction: ChartData[] =
    transformData(conversionByFunction);

  const total_count = statsData?.data?.total_count;
  const accepted = offerData.find(item => item.name == "ACCEPTED")?.value;
  const hireConversionRate = (Number(accepted) / total_count) * 100;

  return (
    <div className="bg-white min-h-screen p-4 sm:p-5">
      <div className="top p-2 bg-revampBlue rounded-md flex flex-col lg:flex-row gap-4 items-start lg:items-center lg: justify-between">
        {/* Left Stats Box */}
        <div className="text-white w-full lg:w-80 flex items-center rounded-md py-5 px-3 gap-x-4">
          <div
            className="w-16 px-2 py-2 rounded-full"
          >
            {/* <BsSearch className="text-5xl text-cGreen px-1" /> */}
            <img src={JobOfferArt} className="w-full object-contain" alt="job-offer-art" />
          </div>
          <div className="flex items-center gap-2">
            <h1 className="text-white text-xl font-medium uppercase">Total Job Offers</h1>
            <p className="text-white font-semibold text-2xl">
              {statsData?.data?.total_count}
            </p>
          </div>
        </div>

        {/* Right Timeline Picker */}
        <div className="max-w-sm flex-1 text-white">
          <label className="text-white text-xs"><span className="text-white">Timeline</span></label>
          <button
            onClick={() => setShowPicker(true)}
            className="text-textColor flex items-center justify-between bg-white px-5 pb-3 rounded w-full"
          >
            <p className="mt-4 text-gray-700">
              {range?.startDate && range?.endDate ? (
                <>
                  {formatFullDate(range.startDate)} -{" "}
                  {formatFullDate(range.endDate)}
                </>
              ) : (
                <span>Select Date Range</span>
              )}
            </p>
            <p className="mt-4">
              <MdKeyboardArrowRight className="text-xl" />
            </p>
          </button>
        </div>
      </div>

      <div className="mt-5 bg-revampLightGray px-5 py-4 rounded-md grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <MultiFilter
          label="Business Unit"
          options={
            filterData?.data?.business_units?.map((unit: any) => ({
              label: unit.name,
              value: unit.id,
            })) || []
          }
          value={businessUnit}
          onChange={setBusinessUnit}
        />
        <MultiFilter
          label="Region"
          disabled={businessUnit.length === 0} // 🔒 Disable if no BU selected
          options={sortedByName(myregion)?.map((d: any) => ({
            value: d.id.toString(),
            label: d.name,
            countries: d.countries,
            business_units: d.business_units,
          }))}
          value={region}
          onChange={setRegion}
        />
        <MultiFilter
          label="Country"
          disabled={region.length === 0} // 🔒 Disable if no Region selected
          options={sortedByName(allCountries).map((unit: any) => ({
            label: unit.name,
            value: unit.id,
          }))}
          value={country}
          onChange={setCountry}
        />

        <MultiFilter
          label="Gender"
          options={[
            { value: "female", label: "Female" },
            { value: "male", label: "Male" },
            { value: "other", label: "Other" },
          ]}
          value={gender}
          onChange={setGender}
        />

        <MultiFilter
          label="Company Job Function"
          options={
            filterData?.data?.company_job_functions?.map((unit: any) => ({
              label: unit.name,
              value: unit.id,
            })) || []
          }
          value={companyJobFunctions}
          onChange={setCompanyJobFunctions}
        />
        <MultiFilter
          label="Grade"
          options={
            filterData?.data?.grades?.map((grade: any) => ({
              label: grade,
              value: grade,
            })) || []
          }
          value={grade}
          onChange={setGrade}
        />
        <MultiFilter
          label="Job Offer Status"
          options={[
            { value: "ACCEPTED", label: "Accepted" },
            { value: "DRAFTED", label: "Drafted" },
            { value: "PLACED", label: "Placed" },
            { value: "REJECTED", label: "Rejected" },
            { value: "EDITED", label: "Edited" },
          ]}
          value={jobOfferStatus}
          onChange={setJobOfferStatus}
        />

        {/* <MultiFilter
          label="Admin"
          options={[
            { value: "saad", label: "Saad" },
            { value: "naba", label: "Naba" },
            { value: "sajjad", label: "Sajjad" },
          ]}
          value={admin}
          onChange={setAdmin}
        /> */}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
        <LineChart
          title="Job Offers By Business Unit"
          color="#2D7DEF"
          data={businessUnitData}
        />
        <LineChart
          title="Job Offers By Region"
          color="#2D7DEF"
          data={regionData}
        />
        <LineChart
          title="Job Offers By Country"
          color="#2D7DEF"
          data={countryData}
        />
        <LineChart
          title="Job Offers By Grade"
          color="#2D7DEF"
          data={gradeData}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-4 mt-6">
        <LineChart
          title="Job Offers By Company Job Functions"
          color="#2D7DEF"
          data={functionData}
        />
        {/* <CircularChart
          title="Job Offers By Gender"
          data={genderData}
          colordata={["1A4F9C", "EF3F3F", "F48E7B"]}
        /> */}

        <PackedBubbleChart
          title="Job Offers By Gender"
          data={genderData}
        />
        {/* <LineChart
          title="Job Offers By Admin"
          color="#1814F3"
          data={adminData}
        /> */}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <ConversionCharts
          title="Conversion Metrics (Accepted vs Rejected Offers) By Grade"
          data={newConversionByGrade}
          colordata={["#2D7DEF", "#EF3F3F"]}
        />
        <ConversionCharts
          title="Conversion Metrics (Accepted vs Rejected Offers) By Company Function"
          data={newConversionByFunction}
          colordata={["#2D7DEF", "#EF3F3F"]}
        />

        <PieCharts
          hireConversionRate={Number(hireConversionRate)}
          title="Job Offer Status"
          data={offerData}
          colordata={[
            "BFC9F2",
            "F7B500",
            "22C55E",
            "EB3349",
            "377DDE",
          ]}
        />
      </div>

      {showPicker && (
        <div className="fixed top-0 inset-0 z-50 bg-black bg-opacity-40 flex justify-center items-center">
          <DateRangeSelector
            initialRange={range}
            onApply={(selected: { startDate: string; endDate: string }) => {
              setRange(selected);
              setShowPicker(false);
            }}
            onCancel={() => setShowPicker(false)}
          />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
